<template>
  <div class="full-wrapper">
    <div class="full" style="height: calc(100vh - 204px);">
      <el-row v-if="!isWidget" type="flex" justify="space-between" :class="['title-bar', 'custom-title-bar']">
        <div>
          <el-select style="width: 150px" size="mini" v-model="txType" @change="loadTransaction">
            <el-option v-for="type in typeList" :key="type.txType" :label="type.label" :value="type.txType" />
          </el-select>
          <el-tooltip effect="dark" content="시작" placement="bottom">
            <el-button v-show="stopTaskFlag" size="mini" @click="restartTask"><i class="fas fa-play" /></el-button>
          </el-tooltip>
          <el-tooltip effect="dark" content="일시중지" placement="bottom">
            <el-button v-show="!stopTaskFlag" size="mini" @click="stopTask"><i class="fas fa-pause" /></el-button>
          </el-tooltip>
          <el-date-picker v-show="stopTaskFlag" v-model="selectedDate" type="date" :picker-options="{ disabledDate }" format="yyyy-MM-dd" :clearable="false" @change="loadTransactionByXStep(xStep)" />
          <!-- 분포드 드래그시 영역 범위 표현 -->
          <!-- <span class="mr-l4">
            <span class="tit">기간</span>
            <span class="high"></span>
            <span class="tit">{{ txTypeLabel }}</span>
            <span class="high"></span>
          </span> -->
        </div>

        <div>
          <el-switch class="mr-r4" v-model="isError" active-text="비정상 트랜잭션만 표시" />
          <!-- <span>
            <el-switch class="mr-r4" v-model="searchIp" active-text="클라이언트 IP" />
            <el-input style="width: 180px;"/>
          </span>
          <span>
            <el-switch class="mr-r4" v-model="searchUri" active-text="URI" />
            <el-input style="width: 180px;"/>
          </span> -->
          <span class="mr-r4" v-show="txType !== 'cpu'">
            <el-tooltip effect="light" :content="getTooltipValueDetail(txLevelY + 1)" placement="bottom">
              <span class="tit">{{ `값 [${txLevelY + 1} 레벨]` }}</span>
            </el-tooltip>
            <el-tooltip effect="light" :content="getTooltipValueDetail(txLevelY)" placement="bottom">
              <el-button :disabled="disabledYLevelDown" @click="downYLevel"><i class="fas fa-search-plus" /></el-button>
            </el-tooltip>
            <el-tooltip v-model="overScaleY" effect="dark" content="표현되지 않은 트랜잭션이 존재합니다." placement="top" manual>
              <el-tooltip effect="light" :content="getTooltipValueDetail(txLevelY + 2)" placement="bottom">
                <el-button :disabled="disabledYLevelUp" @click="upYLevel"><i class="fas fa-search-minus" /></el-button>
              </el-tooltip>
            </el-tooltip>
          </span>

          <el-tooltip effect="light" :content="getTooltipTimeDetail(txLevelX + 1)" placement="bottom">
            <span class="tit">{{ `시간 [${txLevelX + 1} 레벨]` }}</span>
          </el-tooltip>
          <el-tooltip effect="light" :content="getTooltipTimeDetail(txLevelX)" placement="bottom">
            <el-button :disabled="disabledXLevelDown" @click="downXLevel"><i class="fas fa-search-plus" /></el-button>
          </el-tooltip>
          <el-tooltip effect="light" :content="getTooltipTimeDetail(txLevelX + 2)" placement="bottom">
            <el-button :disabled="disabledXLevelUp" @click="upXLevel"><i class="fas fa-search-minus" /></el-button>
          </el-tooltip>
        </div>
      </el-row>

      <!-- 분포도 영역 -->
      <div :class="chartClass">
        <highcharts style="height: 100%" class="fit-highchart" ref="transactionChart" :options="options" />
      </div>

      <!-- 정지했을 경우 x축 이동 가능한 컴포넌트 -->
      <el-row class="slider-bar" v-show="stopTaskFlag" type="flex" justify="space-between">
        <el-button class="mr-r10" :disabled="disabledXStepDown" @click="downXStep"><i class="fas fa-chevron-left" /></el-button>
        <el-slider class="mr-r10" v-model="xStep" :max="maxStep" :format-tooltip="formatXStepTooltip" @change="loadTransactionByXStep" />
        <el-button :disabled="disabledXStepUp" @click="upXStep"><i class="fas fa-chevron-right" /></el-button>
      </el-row>
    </div>
    <w-transaction-detail-popup ref="detailPopup" :scatter-data="scatterChartData" :tx-type="txTypeLabel" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, map, filter, find, findLastIndex, forEach, partialRight, pick, clone, isEmpty, pullAllWith, isEqual, concat } from 'lodash';
import { getDateToString } from '@/utils/date';
import { isTrue } from '@/utils/boolean';
import { convertThousandSeparatorFormat } from '@/utils/number';
import TransactionAPI from '@/api/apm/transaction';
import {transactionTooltipValueMap, transactionTooltipTimeMap} from '@/constants/apm-domain-define';
import WTransactionDetailPopup from './components/popup/TransactionDetailPopup';

const offset = 0.5;
const defaultMaxStep = 24;
const hourToMillisecond = 60 * 60 * 1000; // 1시간
const TX_INTERVAL_X = [1000, 5000, 15000, 30000, 60000];
const TX_COUNT_X = 120;
const heatmapOptions = {
  chart: {
    type: 'heatmap',
    animation: false,
    backgroundColor: 'transparent',
    zoomType: 'xy',
  },
  boost: {
    enabled: true,
    useGPUTranslations: false,
  },
  title: false,
  credits: false,
  xAxis: {
    gridLineWidth: 1,
    lineWidth: 1,
    min: 0,
  },
  yAxis: {
    startOnTick: false,
    title: false,
    min: 0,
  },
  legend: false,
  series: [
    {
      turboThreshold: 10000,
      borderWidth: 1,
      borderColor: 'white',
      stickyTracking: false,
      data: [],
    },
  ],
};

export default {
  name: 'WTransactionMatrix',
  components: { WTransactionDetailPopup },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    defaultParams: Object,
    isWidget: {
      type: Boolean,
      default: false,
    },
    isNative: {
      type: Boolean,
      default: true,
    },
    id: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      options: cloneDeep(heatmapOptions),
      typeList: [
        { label: '소요시간(ms)', txType: 'elapsed' },
        { label: 'CPU 사용률(%)', txType: 'cpu' },
        { label: 'SQL 소요 시간(ms)', txType: 'sqlTime' },
      ],
      txType: 'elapsed',
      isError: false,
      colorSetB: [],
      colorSetR: [],
      scatterChartData: {},
      originalTxData: [],
      taskName: 'txMatrixTask',
      intervalY: null,
      stopTaskFlag: false,
      skeletionX: [],
      skeletionY: [],
      params: {},
      unit: null,
      txLevelX: 0,
      txLevelY: 0,
      selectedDate: new Date(),
      xStep: 23,
      overScaleY: false,
      endDatetime: new Date().getTime(),
      xStepInterval: 0,
      maxStep: defaultMaxStep,
    };
  },
  computed: {
    /**
     * X축 주기 down설정 disabled 여부
     * @return {[type]} [description]
     * @author jda
     */
    disabledXLevelDown() {
      return this.txLevelX < 1;
    },
    /**
     * x축 주기 up 설정 disabled여부 (현재 서비스에서 최대 3까지 허용)
     * @return {[type]} [description]
     * @author jda
     */
    disabledXLevelUp() {
      return this.txLevelX >= 4;
    },
    /**
     * Y축 주기 down설정 disabled 여부
     * @return {[type]} [description]
     * @author jda
     */
    disabledYLevelDown() {
      return this.txLevelY < 1;
    },
    /**
     * Y축 주기 up 설정 disabled여부 (현재 서비스에서 최대 5까지 허용)
     * @return {[type]} [description]
     * @author jda
     */
    disabledYLevelUp() {
      return this.txLevelY >= 5;
    },
    /**
     * x축 이동 left 버튼 disabled여부
     * @return {[type]} [description]
     * @author jda
     */
    disabledXStepDown() {
      return false;
      // return this.xStep === 0;
    },
    /**
     * x축 이동 right 버튼 disabled여부
     * @return {[type]} [description]
     * @author jda
     */
    disabledXStepUp() {
      const selectedDate = clone(this.selectedDate).setHours(0, 0, 0, 0);
      const today = new Date().setHours(0, 0, 0, 0);

      return this.xStep === this.maxStep && selectedDate >= today;
    },
    /**
     * 차트를 감싸는 영역 class지정
     * @return {[type]} [description]
     * @author jda
     */
    chartClass() {
      if (this.isWidget) {
        return 'widget-full-fit-highchart';
      }
      return this.stopTaskFlag ? 'slider-highchart' : 'full-fit-highchart';
    },
    /**
     * 시간 레벨 chnage될 때마다 task interval 변경
     * @return {[type]} [description]
     * @author jda
     */
    taskInterval() {
      return TX_INTERVAL_X[this.txLevelX] / 1000;
    },
    /**
     * 트랜잭션 분포도 > 상세 팝업창에서 표현할 타입 라벨
     * @return {[type]} [description]
     * @author jda
     */
    txTypeLabel() {
      return find(this.typeList, { txType: this.txType }).label;
    },
    ...mapGetters(['selectedNode']),
  },
  watch: {
    /**
     * 트랜잭션 분포 탭 활성 여부
     * @param  {[type]} flag [description]
     * @return {[type]}      [description]
     * @author jda
     */
    active: {
      immediate: true,
      handler(flag) {
        if (flag) {
          const defaultParams = this.defaultParams;
          this.txLevelX = defaultParams.txLevelX;
          this.txLevelY = defaultParams.txLevelY;
          this.txType = defaultParams.txType;
          this.isError = defaultParams.isError;
          this.stopTaskFlag = defaultParams.stopTaskFlag;
          if (defaultParams.stopTaskFlag) {
            this.selectedDate = defaultParams.selectedDate;
            this.xStep = defaultParams.xStep;
            this.maxStep = defaultParams.maxStep;
          }

          // const taskName = (this.id >= 0) ? `${this.taskName}.${this.id}` : this.taskName;

          let taskName = '';


          if (this.id >= 0) {
            taskName = this.isNative ? `${this.taskName}.${this.id}` : `${this.taskName}.${this.id}.1`;
          } else {
            taskName = this.taskName;
          }
          // if (this.taskName === taskName) {
          // }
          this.loadTransaction();
          if (!this.$taskManager.existTask(taskName)) {
            this.taskName = taskName;
            this.$taskManager.createTask(taskName, this.taskInterval, () => {
              this.loadTransaction();
            });
          }
          if (!this.stopTaskFlag) {
            this.$taskManager.startTask(taskName);
          }
        } else {
          this.options.series[0].data = [];
          this.scatterChartData = {};
          // this.stopTaskFlag = true;
          this.overScaleY = false;
          this.$taskManager.removeTask(this.taskName);
          this.setCommonTxData();
        }
      },
    },
    /**
     * 비정상 트랜잭션만 표시할지 여부
     * @param  {Boolean} isError [description]
     * @return {Boolean}         [description]
     * @author jda
     */
    isError(isError) {
      let txData = this.originalTxData;
      if (isError) {
        txData = this.filteringError(txData);
      }
      this.options.series[0].data = txData;
    },
    selectedNode() {
      this.loadTransaction();
    },
    /**
     * 차트를 감싸고있는 영역의 class가 바뀌면 차트를 다시 그린다.(높이를 맞추기 위해)
     * @return {[type]} [description]
     * @author jda
     */
    stopTaskFlag(flag) {
      // xStep 및 endDatetime 초기화
      if (flag) {
        this.setCurrentXStep(new Date().getTime());
        this.loadTransactionByXStep(this.xStep);
        this.params = {}; // 일시 정지 후 첫 로드 해야하기 때문에 파라메터 초기화
      }
      this.$nextTick(() => {
        this.$refs.transactionChart.chart.reflow();
      });
    },
    txLevelX: {
      immediate: true,
      handler(level) {
        // const interval = (TX_INTERVAL_X[level] * TX_COUNT_X) / (60 * 1000); // 1분으로 나눈 값
        // const xStepInterval = Math.floor(interval / 10) * 10 * (60 * 1000); // 1의 자리 버림을 위해 10으로 나눈 후 1분을 곱하여 interval 생성
        const xStepInterval = TX_INTERVAL_X[level] * TX_COUNT_X;
        this.xStepInterval = xStepInterval;

        this.maxStep = ((hourToMillisecond / xStepInterval) * defaultMaxStep) - 1;
      },
    },
  },
  mounted() {
    const me = this;
    Object.assign(this.options.chart, {
      events: {
        selection(e) {
          const minXIdx = parseInt(e.xAxis[0].min + offset, 10);
          const maxXIdx = parseInt(e.xAxis[0].max + offset, 10);

          const minYIdx = parseInt(e.yAxis[0].min + offset, 10);
          const maxYIdx = parseInt(e.yAxis[0].max + offset, 10);

          // selection된 영역에 트랜잭션이 없을 경우 상세 팝업창 미제공
          const includedPoints = [];
          this.series[0].data.forEach((point) => {
            if (point.x >= minXIdx && point.x <= maxXIdx && point.y >= minYIdx && point.y <= maxYIdx) {
              includedPoints.push(point);
            }
          });
          if (includedPoints.length < 1) {
            return false;
          }

          const skeletionX = me.skeletionX;
          const skeletionY = me.skeletionY;

          let scatterData = {
            startDatetime: skeletionX[minXIdx].datetime,
            endDatetime: skeletionX[maxXIdx].datetime,
            from: skeletionY[minYIdx].value,
            to: skeletionY[maxYIdx].value,
            onlyIncludeError: isTrue(me.isError) ? 'Y' : 'N',
            stopTaskFlag: me.stopTaskFlag,
          };
          scatterData = Object.assign(me.defaultParams, scatterData);
          me.scatterChartData = scatterData;
          me.$refs.detailPopup.open();
          return false;
        },
      },
    });

    // 툴팁
    this.options.tooltip = {
      formatter() {
        const point = this.point;
        const xDate = getDateToString(point.datetime, 'YYYY-MM-DD HH:mm:ss');
        const yData = me.unit === 'ms' ? `${(point.y * (me.intervalY / 1000)).toFixed(2)}s` : `${point.y}%`;
        let tooltip = `<span style="color:${point.color}">\u25CF</span> ${xDate}<br /> ${me.txTypeLabel}: <b>${yData}</b> <br /> 전체: <b>${convertThousandSeparatorFormat(point.txCount)}개</b>`;
        if (point.errorCount > 0) {
          tooltip += `<br /> 비정상: <b>${convertThousandSeparatorFormat(point.errorCount)}개</b>`;
        }
        return tooltip;
      },
    };
  },
  beforeDestroy() {
    this.overScaleY = false;
    this.$taskManager.removeTask(this.taskName);
    this.setCommonTxData();
  },
  methods: {
    restartTask() {
      this.stopTaskFlag = false;
      this.loadTransaction();
      this.$taskManager.startTask(this.taskName);
    },
    stopTask() {
      this.stopTaskFlag = true;
      this.$taskManager.stopTask(this.taskName);
    },
    /**
     * 트랜잭션 분포도 로드
     * @return {[type]} [description]
     * @author jda
     */
    loadTransaction() {
      if (!this.active) {
        return;
      }
      // const obj = this.selectedNode.obj;
      // const requestType = obj.objDefineId === 'WAS' ? 'instance' : null;
      // const params = { requestType, dataType: this.txType, objId: obj.objId, endDatetime, txLevelX: this.txLevelX, txLevelY: this.txLevelY };
      const lastRequestTime = new Date().getTime();
      const endDatetime = this.stopTaskFlag ? this.endDatetime : lastRequestTime;

      // TODO: 현재 시간의 트랜잭션만 조회되고 append되는 구조로 바껴야 함. requestRecentData:false일 경우 전체 데이터 조회, true일 경우 현재시간의 트랜잭션 조회
      // const requestRecentData = !(this.stopTaskFlag || isEmpty(this.params)); // 일시정지(사용자 정의 조회)이거나 첫로드(params is empty)일 경우는 false
      const requestRecentData = false;
      const params = Object.assign(clone(this.defaultParams), { dataType: this.txType, endDatetime, txLevelX: this.txLevelX, txLevelY: this.txLevelY, requestRecentData });
      if (requestRecentData) {
        params.startDatetime = this.lastRequestTime; // 현재 데이터만 조회하기 위해 시작 시간은 마지막에 요청한 시간을 바인딩한다.
      }
      this.lastRequestTime = lastRequestTime;
      this.params = params;

      TransactionAPI.getTxMatrixList(params).then((response) => {
        if (response.data.success) {
          const result = response.data.data;
          const headerInfo = result.header;
          this.unit = headerInfo.unitY;

          // data옵션에 트랜잭션 색상 저장
          this.colorSetB = headerInfo.colorSetB;
          this.colorSetR = headerInfo.colorSetR;

          // y축 계산을 위한 interval 저장.
          this.intervalY = headerInfo.intervalY;
          this.overScaleY = headerInfo.overScaleY;

          // 스켈레톤 데이터 저장
          this.skeletionX = headerInfo.skeletionX;
          this.skeletionY = headerInfo.skeletionY;

          // const interval = (headerInfo.intervalX * headerInfo.countX) / (60 * 1000); // 1분으로 나눈 값
          // const xStepInterval = Math.floor(interval / 10) * 10 * (60 * 1000); // 1의 자리 버림을 위해 10으로 나눈 후 1분을 곱하여 interval 생성
          // this.xStepInterval = xStepInterval;
          // this.maxStep = ((hourToMillisecond / xStepInterval) * defaultMaxStep) - 1;
          // this.setCurrentXStep(endDatetime);

          const seriesData = result.body;
          map(seriesData, (data) => {
            const colorList = this.$data[`colorSet${data.colorCode}`];
            data.color = colorList[data.colorIndex];
          });
          if (requestRecentData) {
            const minXData = filter(this.originalTxData, (data) => data.datetime < headerInfo.minX);
            pullAllWith(this.originalTxData, minXData, isEqual); // 가장 과거 데이터 삭제
            this.originalTxData.forEach((data) => {
              data.x -= minXData.length;
            });
            this.originalTxData = concat(this.originalTxData, seriesData);
          } else {
            this.originalTxData = cloneDeep(seriesData);
          }

          let txData = this.originalTxData;
          // 비정상 트랜잭션
          if (this.isError) {
            txData = this.filteringError(txData);
          }
          // console.log(txData);
          this.options.series[0].data = txData;

          const skeletionX = map(headerInfo.skeletionX, partialRight(pick, ['tickName', 'showTickLine']));
          const xCategories = [];
          const xTickPositions = [];
          forEach(skeletionX, (xData, index) => {
            xCategories.push(xData.tickName);
            if (xData.showTickLine) {
              xTickPositions.push(index);
            }
          });
          Object.assign(this.options.xAxis, {
            max: xCategories.length,
            categories: xCategories,
            tickPositions: xTickPositions,
          });

          const skeletionY = map(headerInfo.skeletionY, partialRight(pick, ['tickName', 'showTickLine']));
          const yCategories = [];
          const yTickPositions = [];
          forEach(skeletionY, (yData, index) => {
            yCategories.push(yData.tickName);
            if (yData.showTickLine) {
              yTickPositions.push(index);
            }
          });
          Object.assign(this.options.yAxis, {
            max: findLastIndex(yCategories, (y) => !isEmpty(y)),
            categories: yCategories,
            tickPositions: yTickPositions,
          });
        }
      });
    },
    /**
     * 에러 트랜잭션 필터링
     * @param  {[type]} seriesData [description]
     * @return {[type]}            [description]
     * @author jda
     */
    filteringError(seriesData) {
      return filter(seriesData, (data) => data.errorCount > 0);
    },
    /**
     * x축 주기 다운
     * @return {[type]} [description]
     * @author jda
     */
    downXLevel() {
      this.txLevelX -= 1;
      this.$taskManager.changeInterval(this.taskName, this.taskInterval, !this.stopTaskFlag);

      if (this.stopTaskFlag) {
        this.setCurrentXStep(this.endDatetime);
        this.loadTransactionByXStep(this.xStep);
      } else {
        this.loadTransaction();
      }
    },
    /**
     * x축 주기 up
     * @return {[type]} [description]
     * @author jda
     */
    upXLevel() {
      this.txLevelX += 1;
      this.$taskManager.changeInterval(this.taskName, this.taskInterval, !this.stopTaskFlag);

      if (this.stopTaskFlag) {
        this.setCurrentXStep(this.endDatetime);
        this.loadTransactionByXStep(this.xStep);
      } else {
        this.loadTransaction();
      }
    },
    /**
     * Y축 주기 다운
     * @return {[type]} [description]
     * @author jda
     */
    downYLevel() {
      this.txLevelY -= 1;
      this.loadTransaction();
    },
    /**
     * Y축 주기 up
     * @return {[type]} [description]
     * @author jda
     */
    upYLevel() {
      this.txLevelY += 1;
      this.loadTransaction();
    },
    /**
     * date-picker에서 미래시간은 선택하지 못한다.
     * @param  {[type]} datetime [description]
     * @return {[type]}          [description]
     * @author jda
     */
    disabledDate(datetime) {
      return datetime > Date.now();
    },
    /**
     * task가 정지 했을 떄 X축 slider를 이용하여 과거 데이터를 조회할 수 있다.
     * @param  {[type]} step [description]
     * @return {[type]}      [description]
     * @author jda
     */
    loadTransactionByXStep(step) {
      const lastDatetime = clone(this.selectedDate).setHours(24, 0, 0, 0);
      const max = this.maxStep;
      this.endDatetime = step === max ? lastDatetime : lastDatetime - ((max - step) * this.xStepInterval);
      this.loadTransaction();
    },
    /**
     * 슬라이더 툴팁 표현
     * @param  {[type]} step [description]
     * @return {[type]}      [description]
     * @author jda
     */
    formatXStepTooltip(step) {
      const lastDatetime = clone(this.selectedDate).setHours(24, 0, 0, 0);
      const max = this.maxStep;
      const xStepInterval = this.xStepInterval;
      const endDatetime = step === max ? lastDatetime : lastDatetime - ((max - step) * xStepInterval);
      return `${getDateToString(endDatetime - xStepInterval)} ~ ${getDateToString(endDatetime)}`;
    },
    /**
     * x축 이동 step을 줄인다.
     * @return {[type]} [description]
     * @author jda
     */
    downXStep() {
      this.xStep -= 1;
      if (this.xStep < 0) {
        const selectedDate = clone(this.selectedDate);
        selectedDate.setDate(selectedDate.getDate() - 1);
        this.selectedDate = new Date(selectedDate);
        this.xStep = this.maxStep;
      }

      this.loadTransactionByXStep(this.xStep);
    },
    /**
     * x축 이동 step ++
     * @return {[type]} [description]
     * @author jda
     */
    upXStep() {
      this.xStep += 1;
      if (this.xStep > this.maxStep) {
        const selectedDate = clone(this.selectedDate);
        selectedDate.setDate(selectedDate.getDate() + 1);
        this.selectedDate = new Date(selectedDate);
        this.xStep = 0;
      }
      this.loadTransactionByXStep(this.xStep);
    },
    /**
     * 현재 시간을 포함하는 xStep 계산
     * @author jda
     */
    setCurrentXStep(selectedDatetime) {
      const lastDatetime = clone(this.selectedDate).setHours(24, 0, 0, 0);
      // 상태 유지되는 selectedDate가 현재 날짜보다 과거
      if (selectedDatetime > lastDatetime) {
        return;
      }
      const step = this.maxStep - ((lastDatetime - selectedDatetime) / this.xStepInterval);
      this.xStep = Math.ceil(step);
    },
    /**
     * apm store에 트랜잭션 분포도 파라메터를 저장한다.
     * @author jda
     */
    setCommonTxData() {
      const txData = {
        txType: this.txType,
        txLevelX: this.txLevelX,
        txLevelY: this.txLevelY,
        isError: this.isError,
        stopTaskFlag: this.stopTaskFlag,
      };
      if (this.stopTaskFlag) {
        txData.selectedDate = this.selectedDate;
        txData.xStep = this.xStep;
        txData.maxStep = this.maxStep;
      }
      this.$store.dispatch('setTxMatrixData', txData);
    },
    resize() {
      this.$refs.transactionChart.chart.reflow();
    },
    getTooltipValueDetail(value){
      return transactionTooltipValueMap[value];
    },
    getTooltipTimeDetail(time){
      return transactionTooltipTimeMap[time];
    },
  },
};
</script>

<style lang="scss">

.widget-full-fit-highchart {
  height: 100%;
  width: 100%;
}

.full-fit-highchart {
  height: calc(100% - 38px);
  width: 100%;
}

.slider-highchart {
  height: calc(100% - 76px);
  width: 100%;
}

.full-wrapper {
  .custom-title-bar{
    .el-date-editor.el-input {
      width: 140px;
      .el-input__inner {
        padding-left: 30px !important;
      }
    }
  }
}

.slider-bar {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 5px;
  .el-slider {
    width: calc(100% - 80px);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/apm/transaction-matrix/index.vue